import axios from "axios";

const SOS_API = process.env.REACT_APP_SOS_API_URL;
export const API = `${SOS_API}/api`;

const TOKEN_KEY = "sj_token";

export const getToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setToken = (t) => sessionStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => sessionStorage.removeItem(TOKEN_KEY);

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const api = {
  login: (email, password) =>
    client.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  activeAlerts: () => client.get("/alerts/active").then((r) => r.data),
  getAlert: (id) => client.get(`/alerts/${id}`).then((r) => r.data),
  assign: (id, assigned_to) =>
    client.post(`/alerts/${id}/assign`, { assigned_to }).then((r) => r.data),
  addNote: (id, text) =>
    client.post(`/alerts/${id}/notes`, { text }).then((r) => r.data),
  endAlert: (id, status = "resolved") =>
    client.post(`/alerts/${id}/end`, { status }).then((r) => r.data),
  createAlert: (lat, lng, accuracy = 10) =>
    client.post("/alerts", { lat, lng, accuracy }).then((r) => r.data),
  updateLocation: (id, lat, lng, accuracy = 10) =>
    client.patch(`/alerts/${id}/location`, { lat, lng, accuracy }).then((r) => r.data),
  getFileBlob: (path) => {
    const clean = String(path).replace(/^\/+/, "").replace(/^files\//, "");
    return client.get(`/files/${clean}`, { responseType: "blob" }).then((r) => r.data);
  },
  history: () => client.get("/alerts/history").then((r) => r.data.alerts),
};

// ---- Local backend (team/agents management + agent GPS tracking) ----
const LOCAL_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const local = axios.create({ baseURL: LOCAL_API });

export const agentsApi = {
  list: () => local.get("/agents").then((r) => r.data.agents),
  create: (payload) => local.post("/agents", payload).then((r) => r.data),
  remove: (id) => local.delete(`/agents/${id}`).then((r) => r.data),
  assign: (id, payload) => local.post(`/agents/${id}/assign`, payload).then((r) => r.data),
  advance: (id, target) => local.post(`/agents/${id}/advance`, target || {}).then((r) => r.data),
  updateLocation: (id, payload) => local.post(`/agents/${id}/location`, payload).then((r) => r.data),
  release: (id) => local.post(`/agents/${id}/release`).then((r) => r.data),
  byAlert: (alertId) => local.get(`/agents/by-alert/${alertId}`).then((r) => r.data.agent),
};

export const interventionsApi = {
  create: (payload) => local.post("/interventions", payload).then((r) => r.data),
  list: () => local.get("/interventions").then((r) => r.data.interventions),
};

export const clientsApi = {
  list: () => local.get("/clients").then((r) => r.data.clients),
  create: (payload) => local.post("/clients", payload).then((r) => r.data),
  update: (id, payload) => local.put(`/clients/${id}`, payload).then((r) => r.data),
  remove: (id) => local.delete(`/clients/${id}`).then((r) => r.data),
  byPhone: (phone) => local.get(`/clients/by-phone/${encodeURIComponent(phone)}`).then((r) => r.data.client),
  importFromApp: (payload) => local.post("/clients/import", payload).then((r) => r.data),
};

// ---- Dashboard auth (RBAC admin/manager) ----
const DASH_KEY = "sj_dash_token";
export const getDashToken = () => sessionStorage.getItem(DASH_KEY);
export const setDashToken = (t) => sessionStorage.setItem(DASH_KEY, t);
export const clearDashToken = () => sessionStorage.removeItem(DASH_KEY);

local.interceptors.request.use((config) => {
  const t = getDashToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const dashApi = {
  login: (identifier, password) => local.post("/dash/login", { identifier, password }).then((r) => r.data),
  me: () => local.get("/dash/me").then((r) => r.data.user),
  managers: () => local.get("/dash/managers").then((r) => r.data.managers),
  createManager: (p) => local.post("/dash/managers", p).then((r) => r.data),
  updateManager: (id, p) => local.put(`/dash/managers/${id}`, p).then((r) => r.data),
  removeManager: (id) => local.delete(`/dash/managers/${id}`).then((r) => r.data),
  changePassword: (p) => local.post("/dash/change-password", p).then((r) => r.data),
  logout: () => local.post("/dash/logout").then((r) => r.data),
  activity: () => local.get("/dash/activity").then((r) => r.data.logs),
  subscriptions: () => local.get("/dash/subscriptions").then((r) => r.data.subscriptions),
  createSubscription: (p) => local.post("/dash/subscriptions", p).then((r) => r.data),
  updateSubscription: (id, p) => local.put(`/dash/subscriptions/${id}`, p).then((r) => r.data),
  removeSubscription: (id) => local.delete(`/dash/subscriptions/${id}`).then((r) => r.data),
  financeStats: () => local.get("/dash/finance/stats").then((r) => r.data),
  forgotPassword: (email) => local.post("/dash/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, new_password) => local.post("/dash/reset-password", { token, new_password }).then((r) => r.data),
};

export default client;
